import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClientOptionsWith } from '@/hooks/useClientOptions';
import { OperationKindCards } from './OperationKindCards';
import { IncomingOperationForm } from './IncomingOperationForm';
import { OutgoingOperationForm } from './OutgoingOperationForm';
import { ConversionOperationForm } from './ConversionOperationForm';
import { useNewOperation } from './useNewOperation';
import { OPERATION_KIND, type OperationKind } from './types';
import { ClientPicker } from './ClientPicker';

/**
 * New Operation — books by hand what normally arrives through a channel
 * (#271, #272, #273).
 *
 * Reached from the Monitoring and Incoming screens and from a client's
 * History tab. Arriving with `?clientId=` preselects the client, so the
 * operator coming from a client page does not have to find them again.
 *
 * All three operations are reachable: an incoming wire, a conversion
 * between two of the client's currencies, and an outgoing wire.
 */
export function NewOperationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetClientId = Number(searchParams.get('clientId'));
  const [clientId, setClientId] = useState<number | undefined>(
    Number.isInteger(presetClientId) && presetClientId > 0 ? presetClientId : undefined,
  );

  // One request brings back 500 clients and a tenant can hold more, so the
  // picker searches on the server. The client arrived at from a History tab
  // is fetched by id on top, in case the search does not cover them.
  const [search, setSearch] = useState('');
  const {
    clients,
    preferred,
    isLoading: isLoadingClients,
    isResolving,
  } = useClientOptionsWith(clientId, search);
  const [kind, setKind] = useState<OperationKind>(OPERATION_KIND.INCOMING);

  // Un id que meme cette recherche ne resout pas — perime, recopie a la
  // main — laisserait le selecteur sur « Select a client » tandis que le
  // formulaire, lui, bookerait sur cet id invisible. L'operateur validerait
  // un mouvement d'argent sans voir qui le recoit.
  const isKnownClient = preferred !== null && preferred.id === clientId;
  useEffect(() => {
    if (!isLoadingClients && !isResolving && clientId !== undefined && !isKnownClient) {
      setClientId(undefined);
    }
  }, [isLoadingClients, isResolving, clientId, isKnownClient]);

  const selectedClientId = isKnownClient ? clientId : undefined;

  const goBack = () => navigate(-1);
  const { bookIncoming, bookOutgoing, bookConversion, isBooking } = useNewOperation(goBack);

  return (
    <div data-testid="new-operation-page">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-[#333]">New Operation</h2>
        <p className="mt-1 text-sm text-[#999]">
          Book an operation by hand, with the same wallet entries the usual channel would make
        </p>
      </div>

      <section className="mb-6 rounded-lg border border-[#e0e0e0] bg-white p-6">
        <h3 className="mb-4 text-sm font-semibold text-[#333]">1. Pick the client</h3>
        <ClientPicker
          clients={clients}
          selected={preferred}
          onChange={setClientId}
          onSearchChange={setSearch}
          isLoading={isLoadingClients || isResolving}
        />
      </section>

      <section className="rounded-lg border border-[#e0e0e0] bg-white p-6">
        <h3 className="mb-4 text-sm font-semibold text-[#333]">2. Choose the operation</h3>
        <OperationKindCards value={kind} onChange={setKind} />

        {selectedClientId === undefined ? (
          <p className="mt-6 text-sm text-[#999]" data-testid="operation-no-client">
            Pick a client to enter the operation.
          </p>
        ) : (
          <>
            {kind === OPERATION_KIND.INCOMING && (
              <IncomingOperationForm
                clientId={selectedClientId}
                onSubmit={bookIncoming}
                onCancel={goBack}
                isSubmitting={isBooking}
              />
            )}
            {kind === OPERATION_KIND.CONVERSION && (
              <ConversionOperationForm
                clientId={selectedClientId}
                onSubmit={bookConversion}
                onCancel={goBack}
                isSubmitting={isBooking}
              />
            )}
            {kind === OPERATION_KIND.PAYMENT && (
              <OutgoingOperationForm
                clientId={selectedClientId}
                onSubmit={bookOutgoing}
                onCancel={goBack}
                isSubmitting={isBooking}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}
